import { useEffect, useState } from 'react';
import { useStore } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { isEqual, isNil } from 'lodash';
import Joi from 'joi';

import TransactionTableSetup, { RowSetup } from '../constants/transactionTableSetup';
import { TransactionTypeConfiguration } from '../components/transaction/AddTransactionTypeDialogButton';
import transactionTableColumnSources, { TransactionTableColumnSource } from '../constants/transactionTableColumnSources';
import usePersistedStore from './usePersistedStore';

type ColumnDefinition = {
  index: string;
  source: TransactionTableColumnSource;
  label: string;
}[];

type RowsIdentifier = TransactionTypeConfiguration | undefined;

const transactionTypeConfigurationSchema = Joi.object({
  or: Joi.array().items(Joi.object({
    and: Joi.array().items(Joi.object({
      inclusion: Joi.string().valid('included', 'excluded').required(),
      transactionTypeId: Joi.string(),
    })).required(),
  })).required(),
});

// The "all transactions" row is kept as undefined, which localStorage reads back as null.
// Sparse: values set during the session reach the store unserialized, with undefined intact
const storedRowsIdentifierSchema = Joi.array()
  .items(transactionTypeConfigurationSchema.allow(null))
  .sparse()
  .min(1)
  .required();

export const parseStoredRowsIdentifier = (storedValue: unknown): RowsIdentifier[] | undefined => {
  const { error, value } = storedRowsIdentifierSchema.validate(storedValue);
  if (error) return undefined;

  return (value as (TransactionTypeConfiguration | null)[])
    .map((configuration) => configuration ?? undefined);
};

const getRowsIdentifierFromConfig = (rows: RowSetup[] | undefined) => {
  if (!rows || rows.length === 0) return [undefined];

  const defaultConfiguration: RowsIdentifier[] = [
    undefined,
    ...rows.map((row) => row.transactionTypeConfiguration ?? undefined),
  ];
  return defaultConfiguration;
};

const getSelectedRowFromConfig = (
  savedRowConfiguration: TransactionTableSetup['rows'] | undefined,
  savedRowToShowInGraph: TransactionTableSetup['rowToShowInGraph'] | undefined,
) => {
  if (!savedRowConfiguration || !savedRowToShowInGraph) return undefined;

  const rowToShowInGraph = savedRowConfiguration
    .find((row) => row.id === savedRowToShowInGraph);

  if (!rowToShowInGraph) return undefined;

  if (isNil(rowToShowInGraph.transactionTypeConfiguration)) return undefined;

  return JSON.stringify(rowToShowInGraph.transactionTypeConfiguration);
};

const getSortedColumnDefinition = (
  availableColumns: ColumnDefinition,
) => availableColumns.toSorted((a, b) => Number(a.index) - Number(b.index));

const getAvailableColumnsFromConfig = (
  savedColumnConfiguration: TransactionTableSetup['columns'] | undefined,
  columnDefinition: ColumnDefinition,
) => {
  if (!savedColumnConfiguration) return undefined;

  return columnDefinition.toSorted((a, b) => {
    let indexA = savedColumnConfiguration.findIndex((col) => col.source === a.source);
    if (indexA === -1) indexA = Number(a.index);
    let indexB = savedColumnConfiguration.findIndex((col) => col.source === b.source);
    if (indexB === -1) indexB = Number(b.index);
    return indexA - indexB;
  });
};

const getOmittedFromConfig = (
  savedColumnConfiguration: TransactionTableSetup['columns'] | undefined,
  columnDefinition: ColumnDefinition,
) => {
  if (!savedColumnConfiguration) return undefined;

  return savedColumnConfiguration.map(({ source, enabled }) => {
    if (enabled) return undefined;
    const column = columnDefinition.find((col) => col.source === source);
    return column?.index || undefined;
  }).filter((col) => col !== undefined);
};

const getCurrentColumnsFromConfig = (
  savedColumnConfiguration: TransactionTableSetup['columns'] | undefined,
  columnDefinition: ColumnDefinition,
) => {
  if (!savedColumnConfiguration) return undefined;

  return columnDefinition
    .filter(({ source }) => savedColumnConfiguration
      .find((col) => col.source === source)?.enabled ?? true)
    .toSorted((a, b) => {
      let indexA = savedColumnConfiguration.findIndex((col) => col.source === a.source);
      if (indexA === -1) indexA = Number(a.index);
      let indexB = savedColumnConfiguration.findIndex((col) => col.source === b.source);
      if (indexB === -1) indexB = Number(b.index);
      return indexA - indexB;
    })
    .map((col) => col.index);
};

const useUpdatedTableFromConfig = ({
  preferenceKey,
  defaultConfiguration,
}: {
  preferenceKey: string;
  defaultConfiguration?: TransactionTableSetup;
}) => {
  // Keep typeConfigurations synchronized with the saved configuration
  const [
    typeConfigurations,
    setTypeConfigurations,
  ] = usePersistedStore<RowsIdentifier[]>(
    preferenceKey,
    getRowsIdentifierFromConfig(defaultConfiguration?.rows),
    parseStoredRowsIdentifier,
  );
  useEffect(() => {
    setTypeConfigurations(getRowsIdentifierFromConfig(defaultConfiguration?.rows));
  }, [defaultConfiguration?.rows, setTypeConfigurations]);

  // Keep availableColumns synchronized with the saved configuration and keep track of
  // an immutable columnDefinition
  const [
    availableColumns,
    setAvailableColumns,
  ] = useStore<ColumnDefinition>(`preferences.${preferenceKey}.availableColumns`, undefined);
  const [
    columnDefinition,
    setColumnDefinition,
  ] = useState<ColumnDefinition>(getSortedColumnDefinition(availableColumns ?? []));
  useEffect(() => {
    if (!availableColumns) return;

    const sortedColumns = getSortedColumnDefinition(availableColumns);
    if (!isEqual(sortedColumns, columnDefinition)) {
      setColumnDefinition(sortedColumns);
    }
  }, [availableColumns, columnDefinition]);

  // Keep availableColumns synchronized with the saved configuration
  useEffect(() => {
    const newAvailableColumns = getAvailableColumnsFromConfig(
      defaultConfiguration?.columns,
      columnDefinition,
    );
    if (newAvailableColumns) setAvailableColumns(newAvailableColumns);
  }, [columnDefinition, defaultConfiguration?.columns, setAvailableColumns]);

  // Keep omit synchronized with the saved configuration
  const [, setOmit] = useStore<string[] | undefined>(
    `preferences.${preferenceKey}.omit`,
    getOmittedFromConfig(defaultConfiguration?.columns, columnDefinition),
  );
  useEffect(() => {
    const newOmit = getOmittedFromConfig(defaultConfiguration?.columns, columnDefinition);
    if (newOmit) setOmit(newOmit);
  }, [defaultConfiguration?.columns, setOmit, columnDefinition]);

  // Keep currentColumns synchronized with the saved configuration
  const [, setCurrentColumns] = useStore<string[] | undefined>(
    `preferences.${preferenceKey}.columns`,
    getCurrentColumnsFromConfig(defaultConfiguration?.columns, columnDefinition),
  );
  useEffect(() => {
    const newCurrentColumns = getCurrentColumnsFromConfig(
      defaultConfiguration?.columns,
      columnDefinition,
    );
    if (newCurrentColumns) setCurrentColumns(newCurrentColumns);
  }, [columnDefinition, defaultConfiguration?.columns, setCurrentColumns]);

  // Keep rowToShowInGraph synchronized with the saved configuration
  const { setValue } = useFormContext();
  const rowToShowInGraph = getSelectedRowFromConfig(
    defaultConfiguration?.rows,
    defaultConfiguration?.rowToShowInGraph,
  );
  useEffect(() => {
    setTimeout(() => {
      setValue(transactionTableColumnSources.SELECTED_ROW, rowToShowInGraph);
    }, 0); // Needed to actually update the form value
  }, [rowToShowInGraph, setValue]);

  return {
    typeConfigurations,
    setTypeConfigurations,
  };
};

export default useUpdatedTableFromConfig;
